import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { inr, today, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { BankSelect } from "../BankAccounts";
import { askReason } from "../ApprovalLock";
import { field, label, MethodPicker, SplitRows, BalanceBox, applyResult, rowsTotal } from "./shared";

const EditPaymentForm = ({ value, setValue, onCancel, onSave }) => (
  <div className="space-y-2">
    <MethodPicker value={value.method} testid="editpay-method" size="h-8" onChange={(m) => setValue({ ...value, method: m })} />
    <div className="grid grid-cols-2 gap-2">
      <input data-testid="editpay-amount" type="number" step="0.01" min="0" className={field} placeholder="Amount (₹)" value={value.amount} onChange={(e) => setValue({ ...value, amount: e.target.value })} />
      <input data-testid="editpay-date" type="date" className={field + " num"} value={value.date} onChange={(e) => setValue({ ...value, date: e.target.value })} />
    </div>
    <input data-testid="editpay-reference" className={field} disabled={value.method === "cash"} placeholder={value.method === "cash" ? "No reference needed" : "Reference / UTR no."} value={value.reference} onChange={(e) => setValue({ ...value, reference: e.target.value })} />
    {value.method !== "cash" && <BankSelect testid="editpay-bank" value={value.bank_account} onChange={(v) => setValue({ ...value, bank_account: v })} />}
    <div className="flex gap-2 justify-end">
      <button data-testid="editpay-cancel" onClick={onCancel} className="h-8 px-3 rounded-md border border-input text-xs hover:bg-muted transition-colors">Cancel</button>
      <button data-testid="editpay-save" onClick={onSave} className="h-8 px-3 rounded-md bg-accent text-accent-foreground text-xs font-medium hover:bg-accent/90 transition-colors">Save</button>
    </div>
  </div>
);

const PaymentLine = ({ p, editable, onEdit, onDelete }) => (
  <div className="flex items-center justify-between gap-2">
    <div className="min-w-0">
      <div className="num font-semibold">{inr(p.amount)}<span className="uppercase text-xs text-muted-foreground ml-2">{p.method}</span></div>
      <div className="num text-xs text-muted-foreground truncate">{p.date}{p.reference ? ` · Ref ${p.reference}` : ""}{p.receipt_no ? ` · ${p.receipt_no}` : ""}</div>
    </div>
    {editable && (
      <div className="flex items-center gap-1 shrink-0">
        <button data-testid={`editpay-${p.id}`} onClick={onEdit} title="Edit payment" className="h-8 w-8 rounded-md border border-input flex items-center justify-center text-muted-foreground hover:text-accent transition-colors"><Pencil size={13} /></button>
        <button data-testid={`delpay-${p.id}`} onClick={onDelete} title="Delete payment" className="h-8 w-8 rounded-md border border-input flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={13} /></button>
      </div>
    )}
  </div>
);

const ExistingPayments = ({ pay, payList, user, canWrite, reloadLocks, refresh, onClose }) => {
  const [editingId, setEditingId] = useState(null);
  const [editPay, setEditPay] = useState({ date: today(), method: "cash", amount: "", reference: "" });
  const startEdit = (p) => { setEditingId(p.id); setEditPay({ date: p.date, method: p.method, amount: String(p.amount), reference: p.reference || "", bank_account: p.bank_account || "" }); };
  const saveEdit = async () => {
    if (!(parseFloat(editPay.amount) > 0)) return toast.error("Enter a valid amount");
    if (editPay.method !== "cash" && !editPay.reference.trim()) return toast.error("Reference required for online payments");
    const edit_reason = askReason(user, "editing a payment", `invoice ${pay.invoice_no}`, true);
    if (edit_reason === null) return;
    try {
      const res = await api.put(`/invoices/${pay.id}/payments/${editingId}`, { date: editPay.date, method: editPay.method, amount: parseFloat(editPay.amount), reference: editPay.reference, bank_account: editPay.bank_account || "", edit_reason });
      applyResult(res, "Payment updated", reloadLocks);
      setEditingId(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete this ${inr(p.amount)} payment?`)) return;
    const reason = askReason(user, "deleting a payment", `invoice ${pay.invoice_no}`);
    if (reason === null) return;
    try {
      const res = await api.delete(`/invoices/${pay.id}/payments/${p.id}`, { params: { reason } });
      if (res.data?.status === "pending") { toast.info(res.data.message); reloadLocks(); onClose(); return; }
      toast.success("Payment deleted");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (payList.length === 0) return null;
  return (
    <div data-testid="pay-existing-list" className="space-y-2">
      <div className="text-sm font-medium">Recorded payments ({payList.length})</div>
      {payList.map((p) => (
        <div key={p.id} data-testid={`pay-existing-${p.id}`} className="rounded-md border border-border p-2.5 text-sm">
          {editingId === p.id
            ? <EditPaymentForm value={editPay} setValue={setEditPay} onCancel={() => setEditingId(null)} onSave={saveEdit} />
            : <PaymentLine p={p} editable={canWrite && pay.doc_status !== "cancelled"} onEdit={() => startEdit(p)} onDelete={() => del(p)} />}
        </div>
      ))}
    </div>
  );
};

const AddPaymentForm = ({ hasExisting, payDate, setPayDate, rows, setRows, payNote, setPayNote, balance }) => {
  const total = rowsTotal(rows);
  return (
    <>
      {hasExisting && <div className="text-sm font-medium pt-1">Add another payment</div>}
      <div>
        <label className={label}>Date</label>
        <input data-testid="pay-date" type="date" className={field + " num"} value={payDate} onChange={(e) => setPayDate(e.target.value)} />
      </div>
      <div>
        <label className={label}>Payment split <span className="text-muted-foreground font-normal">(cash + bank / UPI / QR)</span></label>
        <SplitRows rows={rows} setRows={setRows} prefix="pay" addTestid="pay-add-split" addMethod="bank" refPlaceholder="No reference needed" />
      </div>
      <div className="flex justify-between items-center text-sm px-1">
        <span className="text-muted-foreground">Total entered</span>
        <span data-testid="pay-total" className={`num font-bold ${total > balance + 0.001 ? "text-destructive" : "text-foreground"}`}>{inr(total)}</span>
      </div>
      <div>
        <label className={label}>Note</label>
        <input data-testid="pay-note" className={field} value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="optional" />
      </div>
    </>
  );
};

export const PaymentDialog = ({ invoice, onClose, onChanged, user, canWrite, reloadLocks }) => {
  const [pay, setPay] = useState(null);
  const [payList, setPayList] = useState([]);
  const [payDate, setPayDate] = useState(today());
  const [payNote, setPayNote] = useState("");
  const [rows, setRows] = useState([{ method: "cash", amount: "", reference: "" }]);

  const fetchPayments = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}/payments`);
      setPayList(data.payments || []);
      setPay((p) => (p ? { ...p, net: data.net, paid: data.paid, balance: data.balance } : p));
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    setPay(invoice);
    setPayList([]);
    if (!invoice) return;
    setPayDate(today()); setPayNote("");
    setRows([{ method: "cash", amount: String(invoice.balance), reference: "" }]);
    fetchPayments(invoice.id);
  }, [invoice, fetchPayments]);

  const refresh = async () => { await fetchPayments(pay.id); onChanged(); };

  const submit = async () => {
    try {
      const splits = rows.filter((r) => parseFloat(r.amount) > 0)
        .map((r) => ({ method: r.method, amount: Math.round(parseFloat(r.amount) * 100) / 100, reference: r.reference || "", bank_account: r.bank_account || "" }));
      if (splits.length === 0) return toast.error("Enter at least one amount");
      if (splits.some((s) => s.method !== "cash" && !s.reference.trim())) return toast.error("Add a reference number for online (bank/UPI/QR) payments");
      const total = Math.round(splits.reduce((s, r) => s + r.amount, 0) * 100) / 100;
      if (total > pay.balance + 0.001) return toast.error(`Total ₹${total.toFixed(2)} exceeds balance ₹${pay.balance.toFixed(2)}`);
      await api.post(`/invoices/${pay.id}/payments`, { date: payDate, note: payNote, splits });
      toast.success("Payment recorded");
      setPayNote("");
      setRows([{ method: "cash", amount: "", reference: "" }]);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const open = pay && pay.balance > 0.001;
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card">
        <DialogHeader><DialogTitle className="font-display text-2xl">Record Payment · {pay?.invoice_no}</DialogTitle></DialogHeader>
        {pay && (
          <div className="space-y-3">
            <BalanceBox><span className="num font-bold text-destructive">{inr(pay.balance)}</span></BalanceBox>
            <ExistingPayments pay={pay} payList={payList} user={user} canWrite={canWrite} reloadLocks={reloadLocks} refresh={refresh} onClose={onClose} />
            {open ? (
              <AddPaymentForm hasExisting={payList.length > 0} payDate={payDate} setPayDate={setPayDate} rows={rows} setRows={setRows} payNote={payNote} setPayNote={setPayNote} balance={pay.balance} />
            ) : (
              <div data-testid="pay-fully-paid" className="rounded-md bg-[hsl(var(--positive))]/10 text-[hsl(var(--positive))] text-sm font-medium p-3 text-center">This invoice is fully paid. You can edit or delete a recorded payment above.</div>
            )}
          </div>
        )}
        <DialogFooter>
          {open && <button data-testid="pay-save" onClick={submit} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Record payment</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
